// Google Cast Web Sender wrapper.
// The __onGCastApiAvailable handler is registered in public/index.html BEFORE
// the sender script loads, so we can trust window.__jixxeoCastReady flag.

const listeners = new Set();

function notify() { listeners.forEach(fn => { try { fn(getState()); } catch {} }); }

function ctxOr() {
  try { return window.cast?.framework?.CastContext?.getInstance?.(); } catch { return null; }
}

function getState() {
  const ctx = ctxOr(); const chrome = window.chrome;
  if (!ctx || !chrome?.cast) return { ready: false, connected: false };
  const sess = ctx.getCurrentSession();
  const state = ctx.getCastState();
  return {
    ready: !!window.__jixxeoCastReady,
    connected: state === "CONNECTED" || !!sess,
    deviceName: sess?.getCastDevice?.()?.friendlyName || "",
    state
  };
}

function attachContextListeners() {
  const ctx = ctxOr();
  if (!ctx || !window.cast?.framework) return;
  try {
    ctx.addEventListener(window.cast.framework.CastContextEventType.CAST_STATE_CHANGED, notify);
    ctx.addEventListener(window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED, notify);
  } catch {}
}

export function initCast() {
  if (window.__jixxeoCastReady) { attachContextListeners(); notify(); return; }
  window.__jixxeoCastListeners = window.__jixxeoCastListeners || [];
  window.__jixxeoCastListeners.push(() => { attachContextListeners(); notify(); });
}

export function onCastChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function castState() { return getState(); }

function guessContentType(url) {
  const u = (url || "").toLowerCase().split("?")[0];
  if (u.endsWith(".webm")) return "video/webm";
  if (u.endsWith(".mov")) return "video/quicktime";
  if (u.endsWith(".m3u8")) return "application/x-mpegURL";
  if (u.endsWith(".mpd")) return "application/dash+xml";
  if (u.endsWith(".ogv") || u.endsWith(".ogg")) return "video/ogg";
  return "video/mp4";
}

function castErrorMessage(err) {
  if (!err) return "Cast failed";
  if (typeof err === "string") return err;
  // chrome.cast.Error has code + description
  const code = err.code || err.errorCode;
  const desc = err.description || err.reason || err.details || err.message;
  const map = {
    LOAD_MEDIA_FAILED: "The TV couldn't load this video. Try another one.",
    SESSION_ERROR: "Cast session error. Reconnect and try again.",
    CANCEL: "Cast was cancelled.",
    TIMEOUT: "Timed out reaching the TV.",
    API_NOT_INITIALIZED: "Cast isn't ready yet, try again in a moment.",
    RECEIVER_UNAVAILABLE: "No Chromecast found on this network.",
    CHANNEL_ERROR: "Lost connection to the TV.",
  };
  return map[code] || desc || `Cast failed (${code || "unknown"})`;
}

export async function castMedia(videoUrl, title, subtitle, poster) {
  const cast = window.cast, chrome = window.chrome;
  if (!window.__jixxeoCastReady || !cast?.framework || !chrome?.cast) {
    throw new Error("Cast isn't ready. Open in Chrome with a Chromecast on the same Wi-Fi.");
  }
  if (!videoUrl) throw new Error("No video URL to cast");
  // Cast receivers require a public https URL; a relative path won't work.
  if (videoUrl.startsWith("/")) {
    videoUrl = window.location.origin + videoUrl;
  }
  if (videoUrl.startsWith("http://")) {
    // Most modern receivers refuse mixed content. Warn early.
    console.warn("[cast] http:// media may be blocked by receiver; use https");
  }

  const ctx = cast.framework.CastContext.getInstance();
  let session = ctx.getCurrentSession();
  if (!session) {
    await ctx.requestSession();
    session = ctx.getCurrentSession();
  }
  if (!session) throw new Error("No cast device selected");

  const contentType = guessContentType(videoUrl);
  const media = new chrome.cast.media.MediaInfo(videoUrl, contentType);
  media.streamType = chrome.cast.media.StreamType.BUFFERED;

  // MovieMediaMetadata renders a nice title/subtitle card on the TV
  const meta = new chrome.cast.media.MovieMediaMetadata();
  meta.title = title || "Jixxeo Video";
  meta.subtitle = subtitle || "";
  meta.metadataType = chrome.cast.media.MetadataType.MOVIE;
  if (poster) meta.images = [new chrome.cast.Image(poster)];
  media.metadata = meta;

  const req = new chrome.cast.media.LoadRequest(media);
  req.autoplay = true;
  req.currentTime = 0;

  // session.loadMedia takes (req, successCB, errorCB) — wrap in a proper promise
  // so we surface the real error code from the receiver.
  return await new Promise((resolve, reject) => {
    session.loadMedia(req).then(
      () => resolve(true),
      (err) => reject(new Error(castErrorMessage(err)))
    );
  });
}

export async function stopCast() {
  const ctx = ctxOr();
  const session = ctx?.getCurrentSession?.();
  if (session) session.endSession(true);
}

export function isCastSupported() {
  return !!(window.chrome?.cast || window.cast?.framework || window.__jixxeoCastReady);
}
